import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AreaChart, Area, ComposedChart, Bar, Line, ResponsiveContainer,
  XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid, LineChart,
} from "recharts";
import { Loader2, X, BarChart3, LineChart as LineIcon, Eye, Maximize2, Minimize2, Settings2, Cloud, Lock } from "lucide-react";
import { toast } from "sonner";
import { fetchOHLC, fetchInsiderForSymbol } from "@/lib/api";
import {
  computeSMA, computeBollinger, computeRSI, computeMACD,
} from "@/components/analysis/indicators";
import { useChartZoom } from "@/hooks/useChartZoom";
import { useAnalysisSync } from "@/components/analysis/AnalysisSyncContext";
import { useAuth } from "@/contexts/AuthContext";
import TradingViewWidget from "@/components/charts/TradingViewWidget";
import ChartLayoutsButton from "@/components/charts/ChartLayoutsButton";
import KCESignalChart from "@/components/KCESignalChart";

const PERIODS = [
  { id: "3mo", label: "3M" },
  { id: "6mo", label: "6M" },
  { id: "1y",  label: "1Y" },
  { id: "2y",  label: "2Y" },
  { id: "5y",  label: "5Y" },
];

// Indicators — toggleable overlays + sub-panes
const OVERLAY_INDICATORS = [
  { id: "sma20",   label: "SMA 20",  color: "#3FE5DC" },
  { id: "sma50",   label: "SMA 50",  color: "#FFD60A" },
  { id: "sma200",  label: "SMA 200", color: "#FF9500" },
  { id: "bbands",  label: "Bollinger", color: "#A98CFF" },
];
const SUBPANE_INDICATORS = [
  { id: "rsi",  label: "RSI 14"  },
  { id: "macd", label: "MACD"    },
];

const INDICATORS_STORAGE_KEY = "symbol_chart_indicators";

const readIndicators = () => {
  try {
    const raw = localStorage.getItem(INDICATORS_STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
};

// Persist mode across the whole app (KCE chart uses the same key)
const readMode = () => {
  try { return localStorage.getItem("kce_chart_mode") === "candles" ? "candles" : "line"; }
  catch { return "line"; }
};

const fmt = (v) => {
  if (v == null) return "—";
  if (v >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

// Candle shape — interpolates open/close inside the Bar's [low, high] bounding box
const Candle = (props) => {
  const { x, y, width, height, payload } = props;
  if (!payload || payload.o == null || payload.c == null || payload.h == null || payload.l == null) return null;
  const { o, h, l, c } = payload;
  const r = h - l;
  if (r <= 0) return null;
  const cx = x + width / 2;
  const wickW = Math.max(0.6, Math.min(1.6, width * 0.08));
  const bodyW = Math.max(1.2, width * 0.62);
  const bodyX = cx - bodyW / 2;
  const openY  = y + ((h - o) / r) * height;
  const closeY = y + ((h - c) / r) * height;
  const bodyTop = Math.min(openY, closeY);
  const bodyH = Math.max(0.8, Math.abs(closeY - openY));
  const up = c >= o;
  const fill = up ? "#00C805" : "#FF3B30";
  return (
    <g>
      <line x1={cx} x2={cx} y1={y} y2={y + height} stroke={fill} strokeWidth={wickW} />
      <rect x={bodyX} y={bodyTop} width={bodyW} height={bodyH} fill={fill} opacity="0.95" />
    </g>
  );
};

// Recharts-friendly: trade markers rendered via a Line with custom dot.
// We pass each bar a `_tradeY` that is the close price *only on dates with a trade*,
// nothing on the others. Then dot fn paints a colored ring for BUY/SELL/etc.
const TradeDot = ({ cx, cy, payload, onClick }) => {
  if (!payload || payload._tradeAction == null || cx == null || cy == null) return null;
  const isBuy = ["BUY", "ADD", "AWARD"].includes(payload._tradeAction);
  const color = isBuy ? "#00C805" : "#FF3B30";
  return (
    <g
      style={{ cursor: "pointer" }}
      onClick={(e) => { e?.stopPropagation?.(); onClick?.(payload); }}
      data-testid={`trade-dot-${payload._tradeIdx}`}
    >
      <circle cx={cx} cy={cy} r="7" fill={color} opacity="0.18" />
      <circle cx={cx} cy={cy} r="4.5" fill={color} stroke="#050505" strokeWidth="1.2" />
    </g>
  );
};

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="bg-[#0c0c0e]/95 border border-white/15 rounded-md p-2.5 backdrop-blur-md shadow-xl">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1">{label}</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
        <span className="text-white/55">Open</span><span className="num text-white/85 text-right">{fmt(row.o)}</span>
        <span className="text-white/55">High</span><span className="num text-[#00C805] text-right">{fmt(row.h)}</span>
        <span className="text-white/55">Low</span><span className="num text-[#FF3B30] text-right">{fmt(row.l)}</span>
        <span className="text-white/55">Close</span><span className="num text-white text-right">{fmt(row.c)}</span>
      </div>
      {row._tradeAction && (
        <div className="mt-1.5 pt-1.5 border-t border-white/10 text-[11px] text-white/75">
          <span className="font-medium" style={{ color: ["BUY","ADD","AWARD"].includes(row._tradeAction) ? "#00C805" : "#FF3B30" }}>
            {row._tradeAction}
          </span>
          {row._tradeNote ? <span className="text-white/60"> · {row._tradeNote}</span> : null}
        </div>
      )}
    </div>
  );
};

/**
 * SymbolChart — reusable line/candle chart for any ticker.
 *
 * Props:
 *   symbol         (string)   required — ticker, e.g. "SPY"
 *   period         (string)   default "1y"
 *   height         (number)   default 280
 *   trades         (array)    optional — [{date:"YYYY-MM-DD", action:"BUY|SELL|…", est_amount_usd, ticker, note}]
 *                              dots will be plotted at those bar dates
 *   onTradeClick   (fn)       optional — fired when a user clicks a trade dot
 *   showHeader     (bool)     default true — set false when embedding inside another modal that already has a header
 *   defaultPeriod  (string)   default same as period
 */
const SymbolChart = ({
  symbol,
  period: initialPeriod = "1y",
  height = 280,
  trades = [],
  onTradeClick,
  showHeader = true,
  initialExpanded = false,
  embedded = false,
  adaptiveStop = null,        // {stop_price, distance_pct, regime} — LEAPs adaptive-exit overlay
}) => {
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(readMode);
  const [period, setPeriod] = useState(initialPeriod);
  const [indicators, setIndicators] = useState(readIndicators);
  const [showIndicatorMenu, setShowIndicatorMenu] = useState(false);
  const [expanded, setExpanded] = useState(initialExpanded);
  const { user } = useAuth();
  const isPro = !!(user && (user.tier === "pro" || user.is_admin));
  // Engine: compact (recharts) | pro (TradingView) | kce (Kumo Cloud Engine — Pro-only).
  // Persisted across reloads — heavy users will pick "pro" once and stay.
  const [chartEngine, setChartEngine] = useState(() => {
    try {
      const saved = localStorage.getItem("kce_chart_engine") || "compact";
      // Defensive: if a free user previously persisted "kce", fall back to compact.
      return saved === "kce" && !(user?.tier === "pro" || user?.is_admin)
        ? "compact" : saved;
    } catch {
      return "compact";
    }
  });
  useEffect(() => {
    try { localStorage.setItem("kce_chart_engine", chartEngine); } catch { /* ignore */ }
  }, [chartEngine]);

  // TradingView-specific: explicit interval + studies state so saved layouts
  // can override the period-derived defaults. `tvInterval = null` means
  // "derive from period selector"; once a saved layout is applied we set it
  // to the explicit interval until the user changes period.
  const [tvInterval, setTvInterval] = useState(null);
  const [tvStudies, setTvStudies] = useState([
    "MASimple", "MACD", "RSI", "Volume",
  ]);

  // Period-derived TV interval used when the user hasn't overridden via a layout
  const derivedTvInterval = (() => {
    switch (period) {
      case "5d":  return "60";
      case "1mo": return "60";
      case "3mo": return "D";
      case "6mo": return "D";
      case "1y":  return "D";
      case "5y":  return "W";
      case "max": return "W";
      default:    return "D";
    }
  })();
  const activeTvInterval = tvInterval || derivedTvInterval;

  const handleApplyLayout = ({ symbol: layoutSymbol, interval, studies }) => {
    setChartEngine("pro");
    setTvInterval(interval);
    setTvStudies(studies || []);
    // If the layout was saved against a different symbol, route there.
    if (layoutSymbol && layoutSymbol.toUpperCase() !== (symbol || "").toUpperCase()) {
      try {
        const next = new URL(window.location.href);
        next.searchParams.set("symbol", layoutSymbol);
        window.history.replaceState({}, "", next.toString());
        window.dispatchEvent(new Event("kce-symbol-change"));
      } catch {
        /* ignore — same-page navigation only */
      }
    }
  };
  // When the user manually changes period, clear the TV-interval override so
  // the period selector regains control.
  useEffect(() => { setTvInterval(null); }, [period]);

  useEffect(() => {
    try { localStorage.setItem("kce_chart_mode", mode); } catch { /* ignore */ }
  }, [mode]);

  useEffect(() => {
    try {
      localStorage.setItem(INDICATORS_STORAGE_KEY, JSON.stringify([...indicators]));
    } catch { /* ignore */ }
  }, [indicators]);

  // Lock body scroll while fullscreen
  useEffect(() => {
    if (!expanded) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setExpanded(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [expanded]);

  const toggleIndicator = (id) => {
    setIndicators((curr) => {
      const next = new Set(curr);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchOHLC(symbol, period)
      .then((d) => { if (!cancelled) setBars(d.bars || []); })
      .catch(() => { if (!cancelled) setBars([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol, period]);

  // Build a date->trade index map. Snap any trade date to the nearest *prior or equal* bar date.
  const data = useMemo(() => {
    if (!bars.length) return [];

    // ---- Indicator computation (client-side) ----
    const sma20Arr  = indicators.has("sma20")  ? computeSMA(bars, 20)  : null;
    const sma50Arr  = indicators.has("sma50")  ? computeSMA(bars, 50)  : null;
    const sma200Arr = indicators.has("sma200") ? computeSMA(bars, 200) : null;
    const bb        = indicators.has("bbands") ? computeBollinger(bars, 20, 2) : null;
    const rsiArr    = indicators.has("rsi")    ? computeRSI(bars, 14)  : null;
    const macdObj   = indicators.has("macd")   ? computeMACD(bars)     : null;

    const attachIndicators = (b, i) => ({
      ...b,
      _sma20:  sma20Arr  && isFinite(sma20Arr[i])  ? sma20Arr[i]  : null,
      _sma50:  sma50Arr  && isFinite(sma50Arr[i])  ? sma50Arr[i]  : null,
      _sma200: sma200Arr && isFinite(sma200Arr[i]) ? sma200Arr[i] : null,
      _bbUpper: bb && isFinite(bb.upper[i]) ? bb.upper[i] : null,
      _bbLower: bb && isFinite(bb.lower[i]) ? bb.lower[i] : null,
      _bbMid:   bb && isFinite(bb.mid[i])   ? bb.mid[i]   : null,
      _rsi:    rsiArr   && isFinite(rsiArr[i])   ? rsiArr[i]   : null,
      _macd:   macdObj  && isFinite(macdObj.macd[i])   ? macdObj.macd[i]   : null,
      _macdSignal: macdObj && isFinite(macdObj.signal[i]) ? macdObj.signal[i] : null,
      _macdHist:   macdObj && isFinite(macdObj.hist[i])   ? macdObj.hist[i]   : null,
    });

    if (!trades?.length) {
      return bars.map((b, i) => ({ ...attachIndicators(b, i), _range: [b.l, b.h] }));
    }
    const sortedTrades = [...trades]
      .filter((t) => t && t.date)
      .map((t, i) => ({ ...t, _idx: i }))
      .sort((a, b) => a.date.localeCompare(b.date));
    // For each trade find the bar with the closest date <= trade.date
    const tradeByBarIdx = new Map();
    let bi = 0;
    sortedTrades.forEach((t) => {
      while (bi + 1 < bars.length && bars[bi + 1].d <= t.date) bi++;
      if (bars[bi]?.d <= t.date) {
        const existing = tradeByBarIdx.get(bi);
        // Keep the largest-volume trade on this bar to avoid overplotting
        if (!existing || (t.est_amount_usd || 0) > (existing.est_amount_usd || 0)) {
          tradeByBarIdx.set(bi, t);
        }
      }
    });
    return bars.map((b, i) => {
      const t = tradeByBarIdx.get(i);
      return {
        ...attachIndicators(b, i),
        _range: [b.l, b.h],
        _tradeY: t ? b.c : null,
        _tradeAction: t ? (t.action || "BUY") : null,
        _tradeAmount: t ? t.est_amount_usd : null,
        _tradeNote: t ? t.note : null,
        _tradeTicker: t ? t.ticker : null,
        _tradeIdx: t ? t._idx : null,
        _tradeRaw: t || null,
      };
    });
  }, [bars, trades, indicators]);

  const yDomain = useMemo(() => {
    if (!data.length) return ["auto", "auto"];
    const vals = data.flatMap((b) => [b.l, b.h]).filter((v) => v != null);
    if (indicators.has("bbands")) {
      data.forEach((b) => {
        if (b._bbUpper != null) vals.push(b._bbUpper);
        if (b._bbLower != null) vals.push(b._bbLower);
      });
    }
    if (!vals.length) return ["auto", "auto"];
    const min = Math.min(...vals); const max = Math.max(...vals);
    const pad = (max - min) * 0.06;
    return [Math.max(0, min - pad), max + pad];
  }, [data, indicators]);

  // Zoom: index-based (time-series), so all sub-panes (price, RSI, MACD)
  // stay in sync by consuming the same sliced array.
  const zoom = useChartZoom({
    data,
    mode: "index",
    resetDeps: [symbol, period, indicators.size],
  });
  const viewData = zoom.slicedData;

  // Sync-zoom: publish the visible price window to AnalysisSyncContext so
  // sibling charts (e.g. GEXChart) can clamp their strike axis to match.
  // Only fire when sync is toggled on AND the numeric window has actually
  // changed — guard against the new-array-identity-on-every-render trap
  // that would otherwise cause an infinite setState loop.
  const sync = useAnalysisSync();
  const lastWindowRef = useRef({ lo: null, hi: null, sym: null });
  useEffect(() => {
    if (!sync?.setPriceWindow) return;
    if (!sync.syncEnabled) return;
    if (!viewData.length) return;
    let lo = Infinity;
    let hi = -Infinity;
    for (const b of viewData) {
      if (b.c == null) continue;
      if (b.c < lo) lo = b.c;
      if (b.c > hi) hi = b.c;
    }
    if (!isFinite(lo) || !isFinite(hi) || hi <= lo) return;
    const pad = (hi - lo) * 0.04;
    const next = { symbol, lo: lo - pad, hi: hi + pad };
    const prev = lastWindowRef.current;
    // Numeric dedupe — only push if the window actually shifted (>0.01% of range).
    const tol = (hi - lo) * 0.0001;
    if (prev.sym === symbol && Math.abs(prev.lo - next.lo) < tol && Math.abs(prev.hi - next.hi) < tol) {
      return;
    }
    lastWindowRef.current = { lo: next.lo, hi: next.hi, sym: symbol };
    sync.setPriceWindow(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewData, symbol, sync?.syncEnabled]);

  // Recompute Y domain on the visible slice so the chart fits the zoomed range
  const viewYDomain = useMemo(() => {
    if (!viewData.length) return yDomain;
    const vals = viewData.flatMap((b) => [b.l, b.h]).filter((v) => v != null);
    if (indicators.has("bbands")) {
      viewData.forEach((b) => {
        if (b._bbUpper != null) vals.push(b._bbUpper);
        if (b._bbLower != null) vals.push(b._bbLower);
      });
    }
    if (!vals.length) return yDomain;
    const min = Math.min(...vals); const max = Math.max(...vals);
    const pad = (max - min) * 0.06;
    return [Math.max(0, min - pad), max + pad];
  }, [viewData, indicators, yDomain]);

  const hasRSI = indicators.has("rsi");
  const hasMACD = indicators.has("macd");
  const indicatorCount = indicators.size;

  const renderHeader = () => (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <span className="num text-sm font-medium text-white">{symbol}</span>
        <span className="text-[9px] uppercase tracking-[0.18em] text-white/40">
          {data.length} bars
          {trades?.length ? ` · ${trades.length} trades` : ""}
          {indicatorCount ? ` · ${indicatorCount} ind` : ""}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        {/* Compact vs Pro (TradingView) engine toggle */}
        <div
          className="inline-flex items-center gap-0.5 bg-[#050505] border border-[#3FE5DC]/25 rounded-md p-0.5"
          data-testid="symbol-chart-engine-toggle"
        >
          <button
            type="button"
            onClick={() => setChartEngine("compact")}
            data-testid={`symbol-chart-engine-compact-${symbol}`}
            title="Compact chart (faster)"
            className={
              "text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded transition " +
              (chartEngine === "compact"
                ? "bg-white text-black"
                : "text-white/55 hover:text-white")
            }
          >
            Compact
          </button>
          <button
            type="button"
            onClick={() => setChartEngine("pro")}
            data-testid={`symbol-chart-engine-pro-${symbol}`}
            title="TradingView Pro — full indicator + drawing tool suite"
            className={
              "text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded transition inline-flex items-center gap-1 " +
              (chartEngine === "pro"
                ? "bg-[#3FE5DC] text-black"
                : "text-[#3FE5DC]/65 hover:text-[#3FE5DC]")
            }
          >
            <span>Pro</span>
            <span className="text-[8px] opacity-70">TV</span>
          </button>
          <button
            type="button"
            onClick={() => {
              if (!isPro) {
                toast("KCE is a Pro indicator", {
                  description: "Upgrade to overlay the Kumo Cloud Engine canopy + Vapor RSI on this chart.",
                });
                return;
              }
              setChartEngine("kce");
            }}
            data-testid={`symbol-chart-engine-kce-${symbol}`}
            title={isPro
              ? "Kumo Cloud Engine — canopy, vector, vapor overlay"
              : "Pro feature — upgrade to enable KCE overlay"}
            className={
              "text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded transition inline-flex items-center gap-1 " +
              (chartEngine === "kce"
                ? "bg-[#A98CFF] text-black"
                : isPro
                  ? "text-[#A98CFF]/65 hover:text-[#A98CFF]"
                  : "text-white/35 hover:text-white/55")
            }
          >
            {isPro ? <Cloud className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}
            <span>KCE</span>
          </button>
        </div>
        {chartEngine === "pro" && (
          <ChartLayoutsButton
            symbol={symbol}
            interval={activeTvInterval}
            studies={tvStudies}
            onApply={handleApplyLayout}
          />
        )}
        <div className="inline-flex items-center gap-0.5 bg-[#050505] border border-white/10 rounded-md p-0.5">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPeriod(p.id)}
              data-testid={`symbol-chart-period-${p.id}`}
              className={
                "text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded transition " +
                (period === p.id ? "bg-white text-black" : "text-white/55 hover:text-white")
              }
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center gap-0.5 bg-[#050505] border border-white/10 rounded-md p-0.5">
          <button
            type="button"
            onClick={() => setMode("line")}
            data-testid={`symbol-chart-mode-line-${symbol}`}
            title="Line"
            className={
              "inline-flex items-center justify-center w-6 h-6 rounded transition " +
              (mode === "line" ? "bg-white text-black" : "text-white/55 hover:text-white")
            }
          >
            <LineIcon className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => setMode("candles")}
            data-testid={`symbol-chart-mode-candles-${symbol}`}
            title="Candles"
            className={
              "inline-flex items-center justify-center w-6 h-6 rounded transition " +
              (mode === "candles" ? "bg-white text-black" : "text-white/55 hover:text-white")
            }
          >
            <BarChart3 className="w-3 h-3" />
          </button>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowIndicatorMenu((v) => !v)}
            data-testid={`symbol-chart-indicators-${symbol}`}
            title="Indicators"
            className={
              "inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/10 bg-[#050505] transition " +
              (indicatorCount > 0 ? "text-[#3FE5DC]" : "text-white/55 hover:text-white")
            }
          >
            <Settings2 className="w-3.5 h-3.5" />
          </button>
          {showIndicatorMenu && (
            <>
              <div
                className="fixed inset-0 z-[60]"
                onClick={() => setShowIndicatorMenu(false)}
              />
              <div
                className="absolute right-0 top-full mt-1 z-[61] w-52 rounded-md border border-white/10 bg-[#0c0c0e] shadow-2xl p-2"
                data-testid="symbol-chart-indicator-menu"
              >
                <div className="text-[9.5px] uppercase tracking-[0.18em] text-white/40 px-1.5 pb-1.5">
                  Price overlays
                </div>
                {OVERLAY_INDICATORS.map((ind) => (
                  <label
                    key={ind.id}
                    className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white/5 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={indicators.has(ind.id)}
                      onChange={() => toggleIndicator(ind.id)}
                      data-testid={`indicator-toggle-${ind.id}`}
                      className="accent-[#3FE5DC]"
                    />
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-sm"
                      style={{ background: ind.color }}
                    />
                    <span className="text-[11.5px] text-white/85">{ind.label}</span>
                  </label>
                ))}
                <div className="text-[9.5px] uppercase tracking-[0.18em] text-white/40 px-1.5 pt-2 pb-1.5 border-t border-white/8 mt-1">
                  Sub-panes
                </div>
                {SUBPANE_INDICATORS.map((ind) => (
                  <label
                    key={ind.id}
                    className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white/5 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={indicators.has(ind.id)}
                      onChange={() => toggleIndicator(ind.id)}
                      data-testid={`indicator-toggle-${ind.id}`}
                      className="accent-[#3FE5DC]"
                    />
                    <span className="text-[11.5px] text-white/85">{ind.label}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="ml-auto sm:ml-2 hidden sm:flex">
          <zoom.Controls compact />
        </div>
        {!embedded && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`symbol-chart-expand-${symbol}`}
            title={expanded ? "Collapse" : "Expand"}
            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/10 bg-[#050505] text-white/55 hover:text-white transition"
          >
            {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );

  // Effective height — when expanded inside the fullscreen modal we use 60vh
  // for the main pane (we layer sub-panes underneath at fixed heights).
  const mainH = expanded ? Math.max(360, Math.floor(window.innerHeight * 0.55)) : height;
  const subPaneH = expanded ? 140 : 90;

  const renderMainChart = () => (
    <ResponsiveContainer width="100%" height={mainH}>
      <ComposedChart data={viewData} margin={{ top: 6, right: 12, bottom: 4, left: 4 }} {...zoom.chartHandlers}>
        <defs>
          <linearGradient id={`grad-${symbol}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#A98CFF" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#A98CFF" stopOpacity="0" />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="rgba(255,255,255,0.04)" />
        <XAxis dataKey="d" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" minTickGap={32} />
        <YAxis
          domain={viewYDomain}
          tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }}
          stroke="rgba(255,255,255,0.1)"
          width={56}
          tickFormatter={(v) => {
            if (v == null || !isFinite(v)) return "";
            if (Math.abs(v) >= 1000) return v.toFixed(0);
            if (Math.abs(v) >= 100) return v.toFixed(1);
            return v.toFixed(2);
          }}
        />
        <Tooltip content={<Tip />} />

        {mode === "candles" ? (
          <Bar dataKey="_range" shape={<Candle />} isAnimationActive={false} legendType="none" />
        ) : (
          <Area
            type="monotone"
            dataKey="c"
            stroke="#A98CFF"
            strokeWidth={1.6}
            fill={`url(#grad-${symbol})`}
            isAnimationActive={false}
            dot={false}
          />
        )}

        {/* Indicator overlays */}
        {indicators.has("sma20") && (
          <Line type="monotone" dataKey="_sma20" stroke="#3FE5DC" strokeWidth={1.2} dot={false} isAnimationActive={false} legendType="none" connectNulls />
        )}
        {indicators.has("sma50") && (
          <Line type="monotone" dataKey="_sma50" stroke="#FFD60A" strokeWidth={1.2} dot={false} isAnimationActive={false} legendType="none" connectNulls />
        )}
        {indicators.has("sma200") && (
          <Line type="monotone" dataKey="_sma200" stroke="#FF9500" strokeWidth={1.2} dot={false} isAnimationActive={false} legendType="none" connectNulls />
        )}
        {indicators.has("bbands") && (
          <>
            <Line type="monotone" dataKey="_bbUpper" stroke="#A98CFF" strokeWidth={1} strokeDasharray="3 3" dot={false} isAnimationActive={false} legendType="none" connectNulls />
            <Line type="monotone" dataKey="_bbLower" stroke="#A98CFF" strokeWidth={1} strokeDasharray="3 3" dot={false} isAnimationActive={false} legendType="none" connectNulls />
          </>
        )}

        {/* Adaptive Exit stop line — LEAPs Radar's regime-aware ATR trail. */}
        {adaptiveStop?.stop_price != null && (
          <ReferenceLine
            y={adaptiveStop.stop_price}
            stroke="#FF3B30"
            strokeDasharray="5 3"
            strokeWidth={1.4}
            strokeOpacity={0.85}
            label={{
              value: `Ripcord fires · $${adaptiveStop.stop_price.toFixed(2)}${adaptiveStop.distance_pct != null ? ` (${adaptiveStop.distance_pct > 0 ? "-" : "+"}${Math.abs(adaptiveStop.distance_pct).toFixed(1)}%)` : ""}`,
              position: "insideTopRight",
              fill: "#FF3B30",
              fontSize: 10,
              fontWeight: 600,
              offset: 6,
            }}
          />
        )}

        {/* Trade dots overlay — always shown, clickable */}
        {trades?.length > 0 && (
          <Line
            type="monotone"
            dataKey="_tradeY"
            stroke="transparent"
            isAnimationActive={false}
            dot={(props) => <TradeDot {...props} onClick={(p) => onTradeClick?.(p._tradeRaw)} />}
            activeDot={false}
            connectNulls={false}
            legendType="none"
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );

  const renderRSIPane = () => (
    <ResponsiveContainer width="100%" height={subPaneH}>
      <LineChart data={viewData} margin={{ top: 4, right: 12, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.04)" />
        <XAxis dataKey="d" tick={false} stroke="rgba(255,255,255,0.1)" height={0} />
        <YAxis domain={[0, 100]} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" width={56} />
        <Tooltip
          content={({ active, payload, label }) => active && payload?.length ? (
            <div className="bg-[#0c0c0e]/95 border border-white/15 rounded-md px-2.5 py-1.5 text-[11px]">
              <div className="text-white/45 text-[10px] uppercase tracking-[0.18em] mb-0.5">{label}</div>
              <div className="num text-[#FFD60A]">RSI 14 · {payload[0].value?.toFixed?.(1) ?? "—"}</div>
            </div>
          ) : null}
        />
        <ReferenceLine y={70} stroke="#FF3B30" strokeDasharray="3 3" strokeOpacity={0.4} />
        <ReferenceLine y={30} stroke="#00C805" strokeDasharray="3 3" strokeOpacity={0.4} />
        <ReferenceLine y={50} stroke="rgba(255,255,255,0.12)" />
        <Line type="monotone" dataKey="_rsi" stroke="#FFD60A" strokeWidth={1.3} dot={false} isAnimationActive={false} connectNulls />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderMACDPane = () => (
    <ResponsiveContainer width="100%" height={subPaneH}>
      <ComposedChart data={viewData} margin={{ top: 4, right: 12, bottom: 4, left: 4 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.04)" />
        <XAxis dataKey="d" tick={false} stroke="rgba(255,255,255,0.1)" height={0} />
        <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" width={56}
               tickFormatter={(v) => v.toFixed(2)} />
        <Tooltip
          content={({ active, payload, label }) => active && payload?.length ? (
            <div className="bg-[#0c0c0e]/95 border border-white/15 rounded-md px-2.5 py-1.5 text-[11px] space-y-0.5">
              <div className="text-white/45 text-[10px] uppercase tracking-[0.18em]">{label}</div>
              {payload.map((p, i) => (
                <div key={i} className="num" style={{ color: p.color }}>
                  {p.dataKey === "_macd" ? "MACD" : p.dataKey === "_macdSignal" ? "Signal" : "Hist"} · {p.value?.toFixed?.(3) ?? "—"}
                </div>
              ))}
            </div>
          ) : null}
        />
        <ReferenceLine y={0} stroke="rgba(255,255,255,0.18)" />
        <Bar dataKey="_macdHist" isAnimationActive={false}
             fill="#3FE5DC" fillOpacity={0.45} legendType="none" />
        <Line type="monotone" dataKey="_macd"       stroke="#3FE5DC" strokeWidth={1.4} dot={false} isAnimationActive={false} connectNulls />
        <Line type="monotone" dataKey="_macdSignal" stroke="#FF9500" strokeWidth={1.2} dot={false} isAnimationActive={false} connectNulls />
      </ComposedChart>
    </ResponsiveContainer>
  );

  const renderChartBody = () => (
    <div ref={zoom.wheelHostRef}>
      {renderMainChart()}
      {hasRSI && (
        <div className="mt-2 pt-2 border-t border-white/8">
          <div className="text-[9.5px] uppercase tracking-[0.18em] text-[#FFD60A] mb-0.5 px-1">RSI 14</div>
          {renderRSIPane()}
        </div>
      )}
      {hasMACD && (
        <div className="mt-2 pt-2 border-t border-white/8">
          <div className="text-[9.5px] uppercase tracking-[0.18em] text-[#3FE5DC] mb-0.5 px-1">MACD</div>
          {renderMACDPane()}
        </div>
      )}
    </div>
  );

  const chartContent = (
    <div className="space-y-2" data-testid={`symbol-chart-${symbol}`}>
      {showHeader && renderHeader()}

      {chartEngine === "kce" ? (
        <div data-testid={`symbol-chart-kce-${symbol}`}>
          <KCESignalChart symbol={symbol} embedded />
        </div>
      ) : chartEngine === "pro" ? (
        <TradingViewWidget
          symbol={symbol}
          height={expanded ? Math.max(560, Math.floor(window.innerHeight * 0.7)) : Math.max(height, 480)}
          interval={activeTvInterval}
          studies={tvStudies}
        />
      ) : loading ? (
        <div className="flex items-center justify-center text-white/55 text-xs gap-1.5" style={{ height }}>
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      ) : !data.length ? (
        <div className="flex items-center justify-center text-white/45 text-xs" style={{ height }}>
          No data available
        </div>
      ) : (
        renderChartBody()
      )}
    </div>
  );

  if (!expanded) {
    return chartContent;
  }

  // Fullscreen modal
  return (
    <>
      {chartContent /* render once inline to keep state; modal mirrors it */ && null}
      <div
        className="fixed inset-0 z-[90] flex items-start justify-center p-4 sm:p-6 bg-black/85 backdrop-blur-sm"
        onClick={() => setExpanded(false)}
        data-testid={`symbol-chart-fullscreen-${symbol}`}
      >
        <div
          className="relative w-full max-w-6xl bg-[#070710] border border-white/10 rounded-2xl shadow-2xl overflow-hidden mt-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 py-4">
            {chartContent}
          </div>
        </div>
      </div>
    </>
  );
};

// Compact "recent insider activity" panel — Form 4 data via Finnhub (free tier).
// Lazy-loaded so we only fetch when the modal is actually open.
const RecentInsiderPanel = ({ symbol }) => {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchInsiderForSymbol(symbol)
      .then((d) => { if (!cancelled) setItems(d.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol]);

  const top = (items || []).slice(0, 6);
  const fmtUsd = (v) => {
    if (!v) return "—";
    const abs = Math.abs(v);
    if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
    return `$${v.toFixed(0)}`;
  };

  return (
    <div className="mt-5 pt-4 border-t border-white/8" data-testid={`insider-panel-${symbol}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2 flex items-center gap-1.5">
        <Eye className="w-3 h-3" /> Recent insider activity · Form 4 (last 90 d)
      </div>
      {loading ? (
        <div className="text-[11px] text-white/45 inline-flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading insider data…
        </div>
      ) : !top.length ? (
        <div className="text-[11px] text-white/45">No Form 4 filings in the last 90 days.</div>
      ) : (
        <div className="space-y-1">
          {top.map((it, idx) => {
            const isBuy = ["BUY", "AWARD", "EXERCISE"].includes(it.action);
            const color = isBuy ? "#00C805" : it.action === "SELL" ? "#FF3B30" : "#A98CFF";
            return (
              <div
                key={`${it.date}-${idx}`}
                className="grid grid-cols-[78px_70px_minmax(0,1fr)_auto] gap-3 items-center text-[11.5px] py-1"
                data-testid={`insider-row-${idx}`}
              >
                <span className="num text-white/55">{it.date}</span>
                <span
                  className="text-[9px] uppercase tracking-[0.18em] font-semibold px-1.5 py-0.5 rounded border text-center self-start"
                  style={{ borderColor: `${color}55`, background: `${color}12`, color }}
                >
                  {it.action}
                </span>
                <span className="text-white/80 truncate" title={`${it.actor || ""} · ${it.note || ""}`}>
                  {it.actor || "—"} <span className="text-white/40">· {it.note}</span>
                </span>
                <span className="num text-right text-white/85 tabular-nums">{fmtUsd(it.est_amount_usd)}</span>
              </div>
            );
          })}
          {items.length > top.length && (
            <div className="text-[10px] text-white/35 pt-1">
              + {items.length - top.length} more Form 4 filing{items.length - top.length === 1 ? "" : "s"} in the window
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const SymbolChartModal = ({ symbol, open, onClose, trades = [], onTradeClick, title }) => {
  if (!open || !symbol) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="symbol-chart-modal"
    >
      <div
        className="relative w-full max-w-3xl bg-[#070710] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-0.5">
              {title || "Price chart"}
            </div>
            <h3 className="text-xl font-semibold tracking-tight num">{symbol}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-white/55 hover:text-white hover:bg-white/5 transition"
            aria-label="Close"
            data-testid="symbol-chart-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">
          <SymbolChart symbol={symbol} trades={trades} onTradeClick={onTradeClick} height={340} />
          <RecentInsiderPanel symbol={symbol} />
        </div>
      </div>
    </div>
  );
};

export default SymbolChart;
